import React, { useState, useEffect, useRef } from 'react'
import Button from 'storybook/button/button'
import { useForm } from 'react-hook-form'

import { Link } from '@tanstack/react-router'
import TranslationAlerts from 'components/homepage/translation/translation-alerts'
import TranslationTextarea from 'components/basic/translation-textarea'
import Header from 'components/layouts/header/header'
import MainLayout from 'components/layouts/main-layout'
import { TranslationState } from '../lib/interfaces'
import { isThaiText } from '../lib/generic-utils/strings'
import { ConvertEncoding } from '../lib/language.service'
import thai2englishApi from '../api/thai2english-api'
import { makeTranslit } from '../lib/transliteration'
import { getUserSettings } from '../services/user.service'
import TranslitSection from 'components/transliteration/translit-section'
import { UserPrefs } from 'models/user.model'
import defaultUserPrefs from 'lib/defaultUserPrefs'

// Title, description, canonical, hreflang alternates and JSON-LD live on the
// routes' `head`s: /transliteration renders the English variant and
// /ภาษาคาราโอเกะ the Thai one, each passing `lang` down as a prop.

const errorTextEn = 'Please enter some Thai script to transliterate'
const errorTextTh = 'กรุณาพิมพ์เป็นภาษาไทย'

const explainEn = (
   <>
      Automatic Thai to English{' '}
      <Link
         to="/learn/$blogSlug"
         params={{ blogSlug: 'thai-transliteration-the-good-the-bad-and-the-ugly' }}
      >
         transliteration
      </Link>
      . Just enter some Thai script and press the button.
   </>
)
const explainTh = <>แปลภาษาไทยเป็นคาราโอเกะอย่างอัตโนมัติ รวดเร็ว ง่ายๆ</>

const buttonTextEn = 'Transliterate'
const buttonTextTh = 'แปลเป็นภาษาคาราโอเกะ'

const placeholderEn = 'Enter Thai script for transliteration'
const placeholderTh = 'พิมม์ภาษาไทยที่นี่'

interface FormData {
   sourceText: string
}

const Transliteration = ({ lang = 'en' }: { lang?: 'en' | 'th' }) => {
   const { register, handleSubmit, control, reset } = useForm<FormData>()

   const explain = lang === 'th' ? explainTh : explainEn
   const buttonText = lang === 'th' ? buttonTextTh : buttonTextEn
   const placeholder = lang === 'th' ? placeholderTh : placeholderEn

   const textareaRef = useRef<HTMLTextAreaElement | null>(null)
   const [transliterations, setTransliterations] = useState<string[]>([])
   const [translationState, setTranslationState] = useState(TranslationState.None)

   const [theseUserPrefs, setTheseUserPrefs] = useState<UserPrefs>(defaultUserPrefs)
   // This page exists only to romanize, so a visitor who has turned
   // transliteration off everywhere else still gets it here — the site's own
   // scheme stands in. Every other preference (tone markers) is honoured.
   const translit = makeTranslit(
      theseUserPrefs.translitSchemeName === 'none'
         ? { ...theseUserPrefs, translitSchemeName: defaultUserPrefs.translitSchemeName }
         : theseUserPrefs
   )

   useEffect(() => {
      const userPrefs = getUserSettings()
      setTheseUserPrefs(userPrefs)
   }, [])

   // Set focus on first load
   useEffect(() => {
      textareaRef.current?.focus()
   }, [])

   const resetForm = () => {
      setTranslationState(TranslationState.None)
      reset()
      textareaRef.current?.focus()
   }

   const onSubmit = handleSubmit(async (values: FormData) => {
      const { sourceText } = values
      const inputText = ConvertEncoding(sourceText)
      // If there's no text at all, there's nothing we can do
      if (!inputText || !isThaiText(inputText)) {
         const errorText = lang === 'th' ? errorTextTh : errorTextEn
         alert(errorText)
         return
      }

      setTranslationState(TranslationState.Loading)
      try {
         const texts = inputText.split(/(\r)?\n/)
         const queryRes = await thai2englishApi.getTranslations(texts)
         // ConvertEncoding above is the only thing that can put a · in the query
         // (it decodes the legacy &#149; entity), so it can reach the phonetic.
         const phonetics = queryRes.map((r: any) => r.phonetic.replace(/·/g, ''))
         const translits = phonetics.map((p) => translit.of(p))

         setTranslationState(TranslationState.Shown)
         setTransliterations(translits)
      } catch {
         setTranslationState(TranslationState.Error)
      }
   })

   return (
      <MainLayout>
         <Header onClick={() => {}} />
         <div className="container text-center my-20"> {explain}</div>
         <form onSubmit={onSubmit} className="container max-w-5xl mx-auto">
            <TranslationTextarea
               reset={resetForm}
               control={control}
               placeholder={placeholder}
               register={register}
               isLoading={translationState === TranslationState.Loading}
            />
            <div className="mx-auto mb-16 flex max-w-sm">
               <Button size="large" type="button" handleOnClick={onSubmit}>
                  {buttonText}
               </Button>
            </div>
         </form>
         {translationState === TranslationState.Error && (
            <TranslationAlerts errorStatusCode={500} />
         )}
         {translationState === TranslationState.Shown && (
            <TranslitSection translits={transliterations} />
         )}
      </MainLayout>
   )
}

export default Transliteration
